import { useState } from 'react';
import { motion } from 'framer-motion';
import { Upload, Camera, Car, Wrench, AlertTriangle, Check, ArrowRight, ChevronLeft, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const damageTypes = [
  { id: 'accident', label: 'Accident Damage', icon: AlertTriangle },
  { id: 'dent', label: 'Dent', icon: Car },
  { id: 'scratch', label: 'Scratch', icon: Car },
  { id: 'bumper', label: 'Bumper Damage', icon: Car },
  { id: 'paint', label: 'Paint Damage', icon: Car },
  { id: 'body_panel', label: 'Body Panel Damage', icon: Car },
  { id: 'headlight', label: 'Headlight Damage', icon: Car },
  { id: 'mechanical', label: 'Mechanical Problem', icon: Wrench },
  { id: 'warning_light', label: 'Warning Light', icon: AlertTriangle },
  { id: 'engine', label: 'Engine Problem', icon: Wrench },
  { id: 'brake', label: 'Brake Problem', icon: Wrench },
  { id: 'suspension', label: 'Suspension Problem', icon: Wrench },
  { id: 'electrical', label: 'Electrical Problem', icon: Wrench },
  { id: 'not_starting', label: 'Vehicle Not Starting', icon: AlertTriangle },
  { id: 'overheating', label: 'Overheating', icon: AlertTriangle },
  { id: 'other', label: 'Other', icon: Car },
];

const vehicleTypes = [
  'Hatchback', 'Sedan', 'Coupe', 'SUV', 'Bakkie', 'Van', 'Minibus', 'Commercial'
];

export default function RepairQuote() {
  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState('');
  
  const [formData, setFormData] = useState({
    // Vehicle info
    vehicle_make: '',
    vehicle_model: '',
    vehicle_year: '',
    vehicle_registration: '',
    vehicle_colour: '',
    vehicle_type: '',
    // Damage info
    damage_types: [],
    description: '',
    photos: [],
    // Insurance
    is_insurance_claim: '',
    is_driveable: '',
    // Contact
    customer_name: '',
    customer_phone: '',
    customer_whatsapp: '',
    customer_email: '',
    preferred_contact: 'whatsapp',
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleDamageType = (id) => {
    setFormData(prev => ({
      ...prev,
      damage_types: prev.damage_types.includes(id)
        ? prev.damage_types.filter(t => t !== id)
        : [...prev.damage_types, id]
    }));
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    if (formData.photos.length + files.length > 10) {
      toast.error('Maximum 10 photos allowed');
      return;
    }
    
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          photos: [...prev.photos, { file, preview: reader.result }]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.vehicle_make && formData.vehicle_model && formData.vehicle_type;
      case 2:
        return formData.damage_types.length > 0;
      case 3:
        return formData.is_driveable !== '';
      case 4:
        return formData.customer_name && formData.customer_phone && formData.customer_email;
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // Generate reference number
      const year = new Date().getFullYear();
      const random = Math.floor(Math.random() * 100000).toString().padStart(6, '0');
      const ref = `MAF-${year}-${random}`;
      
      // Submit to backend
      await axios.post(`${API}/repair-quotes`, {
        ...formData,
        reference_number: ref,
        photos: formData.photos.map(p => p.preview), // Base64 for now
      });
      
      setReferenceNumber(ref);
      setSubmitted(true);
      toast.success('Repair quote request submitted!');
    } catch (error) {
      console.error('Error submitting quote:', error);
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <main data-testid="repair-quote-success" className="bg-white min-h-screen pt-24">
        <div className="max-w-lg mx-auto px-6 py-16 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-[#1d1d1f] mb-2">
              Repair Request Received
            </h1>
            <p className="text-[#86868b] mb-6">
              Our MA Africa team will review your information and contact you regarding the next assessment step.
            </p>
            <div className="bg-[#f5f5f7] rounded-xl p-4 mb-8">
              <p className="text-sm text-[#86868b] mb-1">Reference Number</p>
              <p className="text-xl font-bold text-[#C41E3A]">{referenceNumber}</p>
            </div>
            <a
              href="/"
              className="inline-flex items-center gap-2 text-[#C41E3A] font-medium hover:underline"
            >
              Return to Home
            </a>
          </motion.div>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="repair-quote-page" className="bg-white min-h-screen pt-20">
      <div className="max-w-2xl mx-auto px-6 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <h1 className="text-3xl md:text-4xl font-bold text-[#1d1d1f] tracking-tight mb-2">
            Request a Repair Quote
          </h1>
          <p className="text-sm text-[#86868b]">
            Tell us about the damage and we'll assess your vehicle.
          </p>
        </motion.div>

        {/* Progress */}
        <div className="flex items-center justify-center gap-2 mb-10">
          {[1, 2, 3, 4].map((step, index) => (
            <div key={step} className="flex items-center gap-2">
              <div 
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                  currentStep === step 
                    ? 'bg-[#C41E3A] text-white' 
                    : currentStep > step 
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-200 text-gray-500'
                }`}
              >
                {currentStep > step ? <Check className="w-4 h-4" /> : step}
              </div>
              {index < 3 && (
                <div className={`w-12 h-1 ${currentStep > step ? 'bg-green-500' : 'bg-gray-200'}`} />
              )}
            </div>
          ))}
        </div>

        {/* Form */}
        <div className="bg-[#f5f5f7] rounded-2xl p-6 md:p-8">
          {/* Step 1: Vehicle Info */}
          {currentStep === 1 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
              <h2 className="text-lg font-semibold text-[#1d1d1f] mb-6">Vehicle Information</h2>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs text-[#86868b] mb-2">Make *</label>
                  <input
                    type="text"
                    value={formData.vehicle_make}
                    onChange={(e) => handleInputChange('vehicle_make', e.target.value)}
                    placeholder="e.g. Toyota"
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white focus:border-[#C41E3A] focus:outline-none"
                    data-testid="vehicle-make"
                  />
                </div>
                <div>
                  <label className="block text-xs text-[#86868b] mb-2">Model *</label>
                  <input
                    type="text"
                    value={formData.vehicle_model}
                    onChange={(e) => handleInputChange('vehicle_model', e.target.value)}
                    placeholder="e.g. Corolla"
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white focus:border-[#C41E3A] focus:outline-none"
                    data-testid="vehicle-model"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs text-[#86868b] mb-2">Year</label>
                  <input
                    type="text"
                    value={formData.vehicle_year}
                    onChange={(e) => handleInputChange('vehicle_year', e.target.value)}
                    placeholder="e.g. 2020"
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white focus:border-[#C41E3A] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs text-[#86868b] mb-2">Registration</label>
                  <input
                    type="text"
                    value={formData.vehicle_registration}
                    onChange={(e) => handleInputChange('vehicle_registration', e.target.value)}
                    placeholder="e.g. ABC 123 GP"
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white focus:border-[#C41E3A] focus:outline-none"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-xs text-[#86868b] mb-2">Vehicle Type *</label>
                <div className="flex flex-wrap gap-2">
                  {vehicleTypes.map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => handleInputChange('vehicle_type', type)}
                      className={`px-4 py-2 rounded-full text-sm transition-all ${
                        formData.vehicle_type === type
                          ? 'bg-[#C41E3A] text-white'
                          : 'bg-white text-[#424245] border border-gray-300 hover:border-[#C41E3A]'
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs text-[#86868b] mb-2">Colour</label>
                <input
                  type="text"
                  value={formData.vehicle_colour}
                  onChange={(e) => handleInputChange('vehicle_colour', e.target.value)}
                  placeholder="e.g. Silver"
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white focus:border-[#C41E3A] focus:outline-none"
                />
              </div>
            </motion.div>
          )}

          {/* Step 2: Damage Type */}
          {currentStep === 2 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
              <h2 className="text-lg font-semibold text-[#1d1d1f] mb-2">Type of Damage/Problem</h2>
              <p className="text-sm text-[#86868b] mb-6">Select all that apply</p>
              
              <div className="grid grid-cols-2 gap-2 mb-6">
                {damageTypes.map((type) => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => toggleDamageType(type.id)}
                    className={`flex items-center gap-2 p-3 rounded-xl text-sm text-left transition-all ${
                      formData.damage_types.includes(type.id)
                        ? 'bg-[#C41E3A] text-white'
                        : 'bg-white text-[#424245] border border-gray-300 hover:border-[#C41E3A]'
                    }`}
                    data-testid={`damage-${type.id}`}
                  >
                    <type.icon className="w-4 h-4 flex-shrink-0" />
                    {type.label}
                  </button>
                ))}
              </div>

              <div>
                <label className="block text-xs text-[#86868b] mb-2">Describe the problem</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Tell us more about the damage or issue..."
                  rows={4}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white focus:border-[#C41E3A] focus:outline-none resize-none"
                  data-testid="damage-description"
                />
              </div>
            </motion.div>
          )}

          {/* Step 3: Photos & Additional Info */}
          {currentStep === 3 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
              <h2 className="text-lg font-semibold text-[#1d1d1f] mb-6">Photos & Details</h2>
              
              {/* Photo Upload */}
              <div className="mb-6">
                <label className="block text-xs text-[#86868b] mb-2">
                  Upload photos (optional, max 10)
                </label>
                <p className="text-xs text-[#86868b] mb-3">
                  Upload clear photos of the affected area. Photos help our team assess the repair before contacting you.
                </p>
                
                <div className="grid grid-cols-4 gap-2 mb-3">
                  {formData.photos.map((photo, index) => (
                    <div key={index} className="relative aspect-square rounded-lg overflow-hidden bg-gray-100">
                      <img src={photo.preview} alt="" className="w-full h-full object-cover" />
                      <button
                        onClick={() => removePhoto(index)}
                        className="absolute top-1 right-1 w-6 h-6 bg-black/50 rounded-full flex items-center justify-center"
                      >
                        <X className="w-3 h-3 text-white" />
                      </button>
                    </div>
                  ))}
                  
                  {formData.photos.length < 10 && (
                    <label className="aspect-square rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center cursor-pointer hover:border-[#C41E3A] transition-colors">
                      <Camera className="w-6 h-6 text-gray-400 mb-1" />
                      <span className="text-xs text-gray-400">Add</span>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handlePhotoUpload}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
              </div>

              {/* Insurance */}
              <div className="mb-6">
                <label className="block text-xs text-[#86868b] mb-2">Is this an insurance claim?</label>
                <div className="flex gap-2">
                  {['Yes', 'No', 'Not sure'].map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => handleInputChange('is_insurance_claim', option.toLowerCase())}
                      className={`flex-1 px-4 py-3 rounded-xl text-sm transition-all ${
                        formData.is_insurance_claim === option.toLowerCase()
                          ? 'bg-[#C41E3A] text-white'
                          : 'bg-white text-[#424245] border border-gray-300 hover:border-[#C41E3A]'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>

              {/* Driveable */}
              <div>
                <label className="block text-xs text-[#86868b] mb-2">Is the vehicle driveable? *</label>
                <div className="flex gap-2">
                  {[{ label: 'Yes, it drives', value: 'yes' }, { label: 'No, not driveable', value: 'no' }].map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleInputChange('is_driveable', option.value)}
                      className={`flex-1 px-4 py-3 rounded-xl text-sm transition-all ${
                        formData.is_driveable === option.value
                          ? 'bg-[#C41E3A] text-white'
                          : 'bg-white text-[#424245] border border-gray-300 hover:border-[#C41E3A]'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 4: Contact */}
          {currentStep === 4 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
              <h2 className="text-lg font-semibold text-[#1d1d1f] mb-6">Your Contact Details</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-xs text-[#86868b] mb-2">Name *</label>
                  <input
                    type="text"
                    value={formData.customer_name}
                    onChange={(e) => handleInputChange('customer_name', e.target.value)}
                    placeholder="Your full name"
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white focus:border-[#C41E3A] focus:outline-none"
                    data-testid="customer-name"
                  />
                </div>

                <div>
                  <label className="block text-xs text-[#86868b] mb-2">Cell Number *</label>
                  <input
                    type="tel"
                    value={formData.customer_phone}
                    onChange={(e) => handleInputChange('customer_phone', e.target.value)}
                    placeholder="076 123 4567"
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white focus:border-[#C41E3A] focus:outline-none"
                    data-testid="customer-phone"
                  />
                </div>

                <div>
                  <label className="block text-xs text-[#86868b] mb-2">WhatsApp Number</label>
                  <input
                    type="tel"
                    value={formData.customer_whatsapp}
                    onChange={(e) => handleInputChange('customer_whatsapp', e.target.value)}
                    placeholder="Same as cell or different"
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white focus:border-[#C41E3A] focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs text-[#86868b] mb-2">Email *</label>
                  <input
                    type="email"
                    value={formData.customer_email}
                    onChange={(e) => handleInputChange('customer_email', e.target.value)}
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white focus:border-[#C41E3A] focus:outline-none"
                    data-testid="customer-email"
                  />
                </div>

                <div>
                  <label className="block text-xs text-[#86868b] mb-2">Preferred contact method</label>
                  <div className="flex gap-2">
                    {['WhatsApp', 'Phone', 'Email'].map((method) => (
                      <button
                        key={method}
                        type="button"
                        onClick={() => handleInputChange('preferred_contact', method.toLowerCase())}
                        className={`flex-1 px-4 py-3 rounded-xl text-sm transition-all ${
                          formData.preferred_contact === method.toLowerCase()
                            ? 'bg-[#C41E3A] text-white'
                            : 'bg-white text-[#424245] border border-gray-300 hover:border-[#C41E3A]'
                        }`}
                      >
                        {method}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-8 pt-6 border-t border-gray-300">
            {currentStep > 1 ? (
              <button
                type="button"
                onClick={() => setCurrentStep(prev => prev - 1)}
                className="flex items-center gap-1 text-sm text-[#86868b] hover:text-[#1d1d1f]"
              >
                <ChevronLeft className="w-4 h-4" />
                Back
              </button>
            ) : (
              <div />
            )}
            
            {currentStep < 4 ? (
              <button
                type="button"
                onClick={() => canProceed() && setCurrentStep(prev => prev + 1)}
                disabled={!canProceed()}
                className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all ${
                  canProceed()
                    ? 'bg-[#C41E3A] text-white hover:bg-[#a01830]'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                Continue
                <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting || !canProceed()}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold bg-[#C41E3A] text-white hover:bg-[#a01830] disabled:opacity-50"
              >
                {submitting ? 'Submitting...' : 'Submit for Assessment'}
                <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
